import type { JobApplicationStatus } from '@cibli/shared';

import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DashboardLayout from '@/features/common/components/DashboardLayout';
import { useUserJobApplications } from '@/hooks/useUserJobApplications';

import { ApplicationCard } from '../components/ApplicationCard';
import { ApplicationFilters, type FilterType } from '../components/ApplicationFilters';
import { EmptyApplicationsState } from '../components/EmptyApplicationsState';

const STATUS_FILTER_MAP: Record<FilterType, JobApplicationStatus[] | null> = {
  all: null,
  active: ['SENT', 'VIEWED'],
  interview: ['INTERVIEW'],
  closed: ['ACCEPTED', 'REJECTED', 'NO_CONTACT'],
};

export default function ApplicationsPage() {
  const { t } = useTranslation();
  const { applications, isLoading, error } = useUserJobApplications();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const filteredApplications = (() => {
    const statuses = STATUS_FILTER_MAP[activeFilter];
    if (!statuses) return applications;
    return applications.filter(app => statuses.includes(app.status));
  })();

  return (
    <DashboardLayout
      title={t('dashboard.applications.title')}
      subtitle={t('dashboard.applications.subtitle')}
    >
      <div>
        <h3 className="font-display text-sm text-cj-green-dark mb-4 lg:hidden">
          {t('dashboard.applications.title')}
        </h3>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <AlertCircle className="h-8 w-8 text-destructive mb-3" />
            <p className="text-sm text-muted-foreground">{t('dashboard.applications.error')}</p>
          </div>
        ) : applications.length === 0 ? (
          <EmptyApplicationsState />
        ) : (
          <>
            <ApplicationFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredApplications.map(app => (
                <ApplicationCard key={app.id} application={app} />
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
